import { useState } from "react";
import { BookOpen, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AzureMock } from "@/components/AzureMock";

const CopyValue = ({ id, label, value }) => {
  const [done, setDone] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(value); setDone(true); toast.success(`${label} copied`); setTimeout(() => setDone(false), 1500); };
  return (
    <div className="flex items-center gap-2" data-testid={`azure-copy-${id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
        <code className="block truncate rounded bg-zinc-100 px-2 py-1 font-mono text-[11px] text-zinc-800">{value}</code>
      </div>
      <button type="button" onClick={copy} data-testid={`azure-copy-${id}-button`}
        className="flex h-7 shrink-0 items-center gap-1 rounded-full border border-zinc-200 bg-white px-2.5 text-[11px] hover:bg-zinc-100">
        {done ? <Check size={11} className="text-emerald-600" /> : <Copy size={11} />}{done ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

const Step = ({ n, title, children, mock }) => (
  <li className="grid gap-4 md:grid-cols-[minmax(0,1fr)_280px]" data-testid={`azure-step-${n}`}>
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-900 font-mono text-[11px] text-white">{n}</span>
        <h3 className="text-sm font-medium text-zinc-900">{title}</h3>
      </div>
      <div className="mt-2 space-y-2 pl-8 text-xs leading-relaxed text-zinc-600">{children}</div>
    </div>
    <AzureMock {...mock} />
  </li>
);

const steps = (redirectUri) => [
  { title: "Open App registrations", mock: { kind: "portal" },
    body: <><p>Sign in at <a href="https://portal.azure.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-700 underline">portal.azure.com <ExternalLink size={10} /></a>, search for <b>App registrations</b> and choose <b>New registration</b>.</p>
      <p>Any Microsoft account works — a personal one is fine.</p></> },
  { title: "Register the app", mock: { kind: "register", redirectUri },
    body: <><CopyValue id="name" label="Name" value="Resonance Harbor" />
      <p>Under <b>Supported account types</b> pick <b>Accounts in any organizational directory and personal Microsoft accounts</b> — this is what lets a personal OneDrive sign in.</p>
      <p>Under <b>Redirect URI</b> choose the platform <b>Web</b> and paste:</p>
      <CopyValue id="redirect" label="Redirect URI (Web)" value={redirectUri} />
      <p>Click <b>Register</b>.</p></> },
  { title: "Copy the Application (client) ID", mock: { kind: "overview" },
    body: <><p>On the app's <b>Overview</b> page copy <b>Application (client) ID</b>. Put it in the server setting:</p>
      <CopyValue id="client-id-key" label="Setting name" value="ONEDRIVE_CLIENT_ID" /></> },
  { title: "Create a client secret", mock: { kind: "secret" },
    body: <><p>Go to <b>Certificates &amp; secrets</b> → <b>New client secret</b>. Pick the longest expiry you're comfortable with (up to 24 months) and click <b>Add</b>.</p>
      <p>Copy the <b>Value</b> column right away — it's shown only once. (Not the Secret ID.) Store it as:</p>
      <CopyValue id="client-secret-key" label="Setting name" value="ONEDRIVE_CLIENT_SECRET" /></> },
  { title: "Add the Graph permissions", mock: { kind: "permissions" },
    body: <><p>Under <b>API permissions</b> → <b>Add a permission</b> → <b>Microsoft Graph</b> → <b>Delegated</b>, tick these and click <b>Add permissions</b>:</p>
      <CopyValue id="scopes" label="Delegated permissions" value="Files.ReadWrite, User.Read, offline_access" />
      <p>No admin consent is needed for a personal account — you'll approve them on first sign-in.</p></> },
  { title: "Add more redirect URIs later", mock: { kind: "auth", redirectUri },
    body: <><p>Every domain you open the app from needs its own entry under <b>Authentication</b> → <b>Web</b> → <b>Add URI</b>, then <b>Save</b>. The app always tells you the exact value in the OneDrive panel.</p>
      <p>After saving, give Microsoft a few minutes, then press <b>Test Microsoft app settings</b> — connect when the dot turns green.</p></> },
];

export const AzureSetupGuide = ({ redirectUri }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="azure-guide-button"
        className="inline-flex items-center gap-1.5 text-xs text-sky-700 underline-offset-2 hover:underline">
        <BookOpen size={13} />Set up your Microsoft app — step-by-step guide
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] w-[min(94vw,56rem)] max-w-none overflow-y-auto" data-testid="azure-guide-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Set up your Microsoft app</DialogTitle>
            <DialogDescription>Six short steps in the Azure Portal. The panels on the right are simplified illustrations of what you'll see — not live screenshots.</DialogDescription>
          </DialogHeader>
          <ol className="mt-2 space-y-8">
            {steps(redirectUri).map((s, i) => <Step key={i} n={i + 1} title={s.title} mock={s.mock}>{s.body}</Step>)}
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
};
